import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { SessionManager } from '../xr/SessionManager'
import { Subtitle } from '../ui/Subtitle'
import { JapanTerrain } from './shared/JapanTerrain'
import { WoodChips } from './shared/WoodChips'
import { SiteMarkers } from './shared/SiteMarkers'
import { Sfx } from './shared/Sfx'
import { ViewRig } from './shared/ViewRig'
import { AerialTiles } from './shared/AerialTiles'
import { TatamiDeck } from './shared/TatamiDeck'
import { NarratorLite } from './shared/NarratorLite'
import { SITES } from './kanjinData'
import type { ProtoBase, ProtoCtx, WorldMode } from './ProtoBase'
import { Proto1 } from './protos/Proto1'
import { Proto2 } from './protos/Proto2'
import { Proto3 } from './protos/Proto3'
import { Proto4 } from './protos/Proto4'
import { Proto5 } from './protos/Proto5'
import { Proto6 } from './protos/Proto6'
import { Proto7 } from './protos/Proto7'
import { Proto8 } from './protos/Proto8'
import { Proto9 } from './protos/Proto9'
import { Proto10 } from './protos/Proto10'
import { Proto11 } from './protos/Proto11'
import { Proto12 } from './protos/Proto12'
import { Proto13 } from './protos/Proto13'
import { Proto14 } from './protos/Proto14'
import { Proto15 } from './protos/Proto15'

const PROTO_CLASSES = [Proto10, Proto11, Proto13, Proto14, Proto15, Proto1, Proto2, Proto3, Proto4, Proto5, Proto6, Proto7, Proto8, Proto9, Proto12] as const
const MENU_LABELS = [
  '★10. そらとぶズームアウト',
  '★11. 日本中から、一畳が組み上がる（5+8）',
  '★13. 六十六寄進地・光のトンネル 🎢',
  '★14. 超光速トンネル（ワープ版）🌌',
  '★15. ワープホール→異空間 🌀',
  '1. 雲海ジオラマ',
  '2. スケール反転',
  '3. 光の御柱',
  '4. 方位の立体音響 🎧',
  '5. 大圏弧＋ニアミス',
  '6. 手渡しの一片',
  '7. ヴィネット球',
  '8. 一畳敷フレーム実体化',
  '9. 勧進帳連動',
  '12. CG図鑑（12棟＋武四郎）',
]

const DEFAULT_CAM = new THREE.Vector3(0, 120, 140)
const DEFAULT_TGT = new THREE.Vector3(0, 0, -5)
const XR_START = new THREE.Vector3(0, 0, 60) // VR既定: 巨大床地図の南に立つ
const BG_MAP = new THREE.Color(0x0d1522)
const BG_VOID = new THREE.Color(0x06080c)

// --- three 基本 ---------------------------------------------------------
const app = document.getElementById('app')!
const renderer = new THREE.WebGLRenderer({ antialias: true })
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.setSize(window.innerWidth, window.innerHeight)
renderer.xr.setFoveation(1)
app.appendChild(renderer.domElement)

const scene = new THREE.Scene()
scene.background = BG_MAP

const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.05, 9000)
camera.position.copy(DEFAULT_CAM)

const controls = new OrbitControls(camera, renderer.domElement)
controls.target.copy(DEFAULT_TGT)
controls.enableDamping = true
controls.dampingFactor = 0.08

scene.add(new THREE.HemisphereLight(0xfff4e0, 0x1a2434, 1.1))
const dir = new THREE.DirectionalLight(0xffe8c0, 1.4)
dir.position.set(60, 120, 40)
scene.add(dir)

// ブルーム（PC描画のみ。XRはコンポーザ非対応のため素通し）
const composer = new EffectComposer(renderer)
composer.addPass(new RenderPass(scene, camera))
const bloom = new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), 0.75, 0.6, 0.62)
composer.addPass(bloom)

// --- 視点リグ・XR ---------------------------------------------------------
const view = new ViewRig(camera, controls) // camera は rig の子になる
scene.add(view.rig)

const deck = new TatamiDeck()
view.rig.add(deck.root)

// 武四郎アバター（リグ随伴。Proto10/12・VRで表示）
const narrator = new NarratorLite()
view.rig.add(narrator.root)
narrator.root.position.copy(narrator.homePos)
narrator.root.rotation.y = narrator.homeRotY
narrator.root.visible = false
void narrator.load('./assets/takeshiro_rigged.glb').catch(() => {})

const sessionManager = new SessionManager(renderer)
const subtitle = new Subtitle(camera) // VR内字幕（カメラ追従板）

// --- 共有オブジェクト ----------------------------------------------------
const terrain = new JapanTerrain()
const markers = new SiteMarkers()
const chips = new WoodChips(240)
const sfx = new Sfx()
const aerial = new AerialTiles()
aerial.root.visible = false
scene.add(chips.root, aerial.root)

// --- HTML UI -------------------------------------------------------------
const captionEl = document.getElementById('caption')!
const descEl = document.getElementById('desc')!
const ledgerEl = document.getElementById('ledger')!
const ledgerList = document.getElementById('ledgerList')!
const menuEl = document.getElementById('menu')!
const enterVrBtn = document.getElementById('enterVr') as HTMLButtonElement

for (const site of SITES) {
  const row = document.createElement('div')
  row.className = 'lrow'
  row.dataset.no = String(site.no)
  row.innerHTML = `<span class="seal"></span><span class="lname">${site.no}　${site.piece}</span>`
  ledgerList.appendChild(row)
}

function setCaption(text: string): void {
  captionEl.textContent = text
  captionEl.classList.toggle('show', text.length > 0)
  if (view.xrActive) {
    if (text) subtitle.show(text, 8)
    else subtitle.clear()
  }
}
function setLedger(visible: boolean): void {
  ledgerEl.classList.toggle('hidden', !visible)
}
function stampLedger(no: number): void {
  ledgerList.querySelector(`[data-no="${no}"]`)?.classList.add('stamped')
}
function resetLedger(): void {
  ledgerList.querySelectorAll('.lrow').forEach((r) => r.classList.remove('stamped'))
}
function setWorld(mode: WorldMode): void {
  const map = mode === 'map'
  scene.background = (map ? BG_MAP : BG_VOID).clone()
  terrain.root.visible = map
  markers.root.visible = map
  aerial.root.visible = false
}

const ctx: ProtoCtx = {
  scene, camera, controls, renderer, terrain, markers, chips, sfx, view, aerial, deck, narrator,
  setWorld, setCaption, setLedger, stampLedger, resetLedger,
}

// --- プロト切替 ------------------------------------------------------------
let current: ProtoBase | undefined
let currentIndex = -1

function resetDefaults(): void {
  chips.onArrive = undefined
  chips.clear()
  scene.fog = null
  terrain.resetTransforms()
  terrain.root.rotation.set(0, 0, 0)
  markers.root.visible = true
  markers.root.scale.setScalar(1)
  markers.root.position.set(0, 0, 0)
  markers.root.rotation.set(0, 0, 0)
  markers.setLabelsVisible(true)
  markers.clearHighlights()
  controls.enabled = !view.xrActive
  view.reset(DEFAULT_CAM, DEFAULT_TGT, XR_START)
  deck.setVisible(view.xrActive)
  if (narrator.loaded) {
    narrator.restoreHome(view.rig)
    narrator.root.visible = view.xrActive
  }
  aerial.root.scale.setScalar(1)
  aerial.root.position.set(0, 0, 0)
  setLedger(false)
  setCaption('')
}

function select(i: number): void {
  current?.end()
  resetDefaults()
  currentIndex = i
  current = new PROTO_CLASSES[i](ctx)
  descEl.textContent = current.desc
  menuEl.querySelectorAll('button.proto').forEach((b, k) => b.classList.toggle('active', k === i))
  current.begin()
}

const replayBtn = document.getElementById('replay')!
MENU_LABELS.forEach((label, i) => {
  const b = document.createElement('button')
  b.className = 'proto'
  b.textContent = label
  b.addEventListener('click', () => {
    sfx.ensure()
    select(i)
  })
  menuEl.insertBefore(b, replayBtn)
})
replayBtn.addEventListener('click', () => {
  sfx.ensure()
  if (currentIndex >= 0) select(currentIndex)
})
window.addEventListener('pointerdown', () => sfx.ensure(), { once: true })

// --- VR入場・コントローラ --------------------------------------------------
for (const i of [0, 1]) {
  const controller = renderer.xr.getController(i)
  view.rig.add(controller)
  controller.addEventListener('selectstart', () => {
    // 右手（index0が右とは限らないため両手同処理）: トリガー=次のプロトへ
    if (i === 0) select((currentIndex + 1) % PROTO_CLASSES.length)
    else select(currentIndex) // もう一方のトリガー=リプレイ
  })
  controller.addEventListener('squeezestart', () => select(currentIndex)) // グリップ=リプレイ
}

enterVrBtn.addEventListener('click', async () => {
  sfx.ensure()
  const mode = await sessionManager.enter(false) // VR優先
  if (mode === 'none') {
    setCaption('VRセッションを開始できません（Quest 3のブラウザで開いてください）')
    return
  }
  view.xrActive = true
  deck.setVisible(true)
  controls.enabled = false
  select(Math.max(0, currentIndex)) // 現プロトをXR視点で再適用
  subtitle.show('トリガー=次の演出 / グリップ=リプレイ', 7)
})

sessionManager.onEnd = () => {
  view.xrActive = false
  deck.setVisible(false)
  controls.enabled = true
  select(Math.max(0, currentIndex))
}

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight
  camera.updateProjectionMatrix()
  renderer.setSize(window.innerWidth, window.innerHeight)
  composer.setSize(window.innerWidth, window.innerHeight)
})

// --- 起動 -------------------------------------------------------------------
async function boot(): Promise<void> {
  setCaption('日本地図を読み込み中…')
  await terrain.load('./assets/japan_lines.json')
  scene.add(terrain.root)
  markers.build()
  scene.add(markers.root)
  void markers.enhance().catch(() => {}) // 写真プレート＋Meshy製GLB（存在すれば差し替え）
  setCaption('')

  const support = await sessionManager.detect()
  enterVrBtn.disabled = !(support.vr || support.ar)

  select(0) // 初期表示: ★10 そらとぶズームアウト
}

const clock = new THREE.Clock()
renderer.setAnimationLoop(() => {
  const dt = Math.min(clock.getDelta(), 0.1)
  terrain.update(dt)
  chips.update(dt)
  narrator.update(dt)
  current?.update(dt)
  subtitle.update(dt)
  view.update(dt) // 無操作時のシネマティックドリフト
  if (!view.xrActive) controls.update()
  if (renderer.xr.isPresenting) renderer.render(scene, camera)
  else composer.render()
})

boot().catch((e) => {
  console.error(e)
  setCaption(`読み込みエラー: ${e?.message ?? e}`)
})
